/**
 * CsvCellHighlightLayer — helpers + hook for cell / row / column highlights on the CSV viewer.
 *
 * This is not a literal overlay layer: highlights need to render as background colors inside the
 * `<td>` elements so they sit behind the cell text. The hook returns lookup helpers that the
 * table cells consume directly, plus a click handler that toggles highlights when in `highlight`
 * mode. Persistence runs through `csvCellHighlightService` (csvCellHighlights collection).
 */

import { useCallback, useMemo } from 'react';
import type { CsvCellHighlight, CsvHighlightScope } from '../../types';
import { createCellHighlight, deleteCellHighlight } from '../../utils/csvCellHighlightService';

export interface UseCsvHighlightsArgs {
  documentId: string;
  documentUrl: string;
  leadId: string;
  projectId?: string;
  highlights: CsvCellHighlight[];
  /** Active mode + tool from the viewer mode bar — clicks only register when active. */
  enabled: boolean;
  scope: CsvHighlightScope;
  color: string;
  /** Header labels, keyed by column index, used to record `colKey` for resilience. */
  headers: string[];
  currentUser: { id: string; name?: string; email?: string } | null | undefined;
}

export interface CsvHighlightLookup {
  /** Combined background color for a body cell (row/col scoped highlights merged with cell-level). */
  cellBg: (row: number, col: number) => string | undefined;
  /** Background color for the header cell of a column (for column-scoped highlights). */
  headerBg: (col: number) => string | undefined;
  /** Highlights matching a body cell (sorted oldest → newest). Used to pick which one to delete. */
  highlightsAt: (row: number, col: number) => CsvCellHighlight[];
  /** Highlights matching a header cell (column-scoped). */
  highlightsAtHeader: (col: number) => CsvCellHighlight[];
}

export interface UseCsvHighlightsResult extends CsvHighlightLookup {
  /** Click on a body cell. Toggles a highlight per the current `scope` + `color`. */
  onCellClick: (row: number, col: number) => Promise<void>;
  /** Click on a header cell. Only active when `scope === 'column'`. */
  onHeaderClick: (col: number) => Promise<void>;
}

/** Most-recent matching highlight wins (later writes paint over earlier ones). */
function pickColor(matches: CsvCellHighlight[]): string | undefined {
  if (matches.length === 0) return undefined;
  return matches[matches.length - 1].color;
}

export function useCsvHighlights(args: UseCsvHighlightsArgs): UseCsvHighlightsResult {
  const { documentId, documentUrl, leadId, projectId, highlights, enabled, scope, color, headers, currentUser } = args;

  const lookup = useMemo<CsvHighlightLookup>(() => {
    const byCell = new Map<string, CsvCellHighlight[]>();
    const byRow = new Map<number, CsvCellHighlight[]>();
    const byCol = new Map<number, CsvCellHighlight[]>();
    const byColKey = new Map<string, CsvCellHighlight[]>();

    for (const h of highlights) {
      if (h.scope === 'cell' && typeof h.row === 'number' && typeof h.col === 'number') {
        const key = `${h.row}:${h.col}`;
        const arr = byCell.get(key) || [];
        arr.push(h);
        byCell.set(key, arr);
      } else if (h.scope === 'row' && typeof h.row === 'number') {
        const arr = byRow.get(h.row) || [];
        arr.push(h);
        byRow.set(h.row, arr);
      } else if (h.scope === 'column') {
        if (typeof h.col === 'number') {
          const arr = byCol.get(h.col) || [];
          arr.push(h);
          byCol.set(h.col, arr);
        }
        if (h.colKey) {
          const arr = byColKey.get(h.colKey) || [];
          arr.push(h);
          byColKey.set(h.colKey, arr);
        }
      }
    }

    const colMatches = (col: number): CsvCellHighlight[] => {
      const direct = byCol.get(col) || [];
      const headerKey = headers[col];
      if (!headerKey) return direct;
      const byKey = (byColKey.get(headerKey) || []).filter(h => !direct.includes(h));
      return [...direct, ...byKey].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    };

    return {
      cellBg: (row, col) => {
        const matches = [
          ...(byRow.get(row) || []),
          ...colMatches(col),
          ...(byCell.get(`${row}:${col}`) || []),
        ].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        return pickColor(matches);
      },
      headerBg: col => pickColor(colMatches(col)),
      highlightsAt: (row, col) => {
        const matches = [
          ...(byRow.get(row) || []),
          ...colMatches(col),
          ...(byCell.get(`${row}:${col}`) || []),
        ];
        return matches.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      },
      highlightsAtHeader: col => colMatches(col),
    };
  }, [highlights, headers]);

  const onCellClick = useCallback(
    async (row: number, col: number) => {
      if (!enabled || !currentUser?.id) return;
      const matchesScopeAtCell = (h: CsvCellHighlight): boolean => {
        if (scope === 'cell') return h.scope === 'cell' && h.row === row && h.col === col;
        if (scope === 'row') return h.scope === 'row' && h.row === row;
        if (scope === 'column') {
          if (h.scope !== 'column') return false;
          if (h.col === col) return true;
          const headerKey = headers[col];
          return !!headerKey && h.colKey === headerKey;
        }
        return false;
      };
      const existing = lookup.highlightsAt(row, col).filter(matchesScopeAtCell);
      if (existing.length > 0) {
        await Promise.all(
          existing.map(h => (h.id ? deleteCellHighlight(h.id) : Promise.resolve())),
        );
        return;
      }
      const payload: Omit<CsvCellHighlight, 'id'> = {
        documentId,
        documentUrl,
        leadId,
        ...(projectId ? { projectId } : {}),
        scope,
        color,
        userId: currentUser.id,
        userName: currentUser.name || currentUser.email || 'Unknown',
        createdAt: new Date(),
      };
      if (scope === 'cell') {
        payload.row = row;
        payload.col = col;
        payload.colKey = headers[col];
      } else if (scope === 'row') {
        payload.row = row;
      } else if (scope === 'column') {
        payload.col = col;
        payload.colKey = headers[col];
      }
      await createCellHighlight(payload);
    },
    [enabled, currentUser, lookup, scope, color, documentId, documentUrl, leadId, projectId, headers],
  );

  const onHeaderClick = useCallback(
    async (col: number) => {
      if (!enabled || !currentUser?.id || scope !== 'column') return;
      const existing = lookup.highlightsAtHeader(col).filter(h => h.scope === 'column');
      if (existing.length > 0) {
        await Promise.all(
          existing.map(h => (h.id ? deleteCellHighlight(h.id) : Promise.resolve())),
        );
        return;
      }
      await createCellHighlight({
        documentId,
        documentUrl,
        leadId,
        ...(projectId ? { projectId } : {}),
        scope: 'column',
        col,
        colKey: headers[col],
        color,
        userId: currentUser.id,
        userName: currentUser.name || currentUser.email || 'Unknown',
        createdAt: new Date(),
      });
    },
    [enabled, currentUser, scope, lookup, documentId, documentUrl, leadId, projectId, color, headers],
  );

  return {
    ...lookup,
    onCellClick,
    onHeaderClick,
  };
}
